import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StoryMedia from '../stories/StoryMedia';
import StoryVideoPlayer from '../media/StoryVideoPlayer';
import { RADIUS, clipSeconds } from './messagesModel';
import { formatDuration } from '../VoiceRecorder';
import usePageScrollLock from '../../utils/usePageScrollLock';
import useSwipeToDismiss from '../../hooks/useSwipeToDismiss';

/** The item shape StoryMedia plays, from a message. */
export function clipItem(msg) {
  return {
    kind: 'video',
    mediaUrl: msg.videoUrl,
    thumbnailUrl: msg.videoThumbnailUrl,
    trimStartMs: msg.videoTrimStartMs,
    trimEndMs: msg.videoTrimEndMs,
    durationSec: msg.videoDurationSec,
    width: msg.videoWidth,
    height: msg.videoHeight,
    storyboardUrl: msg.videoStoryboardUrl,
    streamUid: msg.videoStreamUid,
  };
}

/**
 * A clip in the thread. Its own frame like a photo: the poster with a play mark and the
 * length, then — once the server says ready — the clip itself, muted, looping inside its
 * trim window. A tap opens it full screen with sound. While it is still going up or
 * encoding the frame says so and nothing plays.
 */
export default function VideoMessageCard({ msg, onOpen, inFlight = false, progress = null }) {
  const status = msg.videoStatus || 'ready';
  const ready = status === 'ready' && !!msg.videoUrl;
  const secs = clipSeconds(msg);
  const portrait = (msg.videoHeight || 0) > (msg.videoWidth || 0);
  const w = portrait ? 200 : 280;
  const h = portrait ? 300 : 180;
  const line = status === 'uploading' ? `SENDING${progress != null ? ` · ${progress}%` : ''}`
    : status === 'encoding' ? 'PROCESSING'
      : status === 'failed' ? 'DIDN’T GO THROUGH'
        : secs ? formatDuration(secs) : '';
  return (
    <button
      type="button"
      data-testid="video-message"
      data-status={status}
      onClick={(e) => { e.stopPropagation(); if (ready) onOpen?.(msg); }}
      disabled={!ready}
      className="relative block"
      aria-label={ready ? `Play video${secs ? `, ${secs} seconds` : ''}` : line}
      style={{ width: w, maxWidth: '100%', height: h, borderRadius: RADIUS.bubble, overflow: 'hidden', background: '#000', opacity: inFlight ? 0.7 : 1, cursor: ready ? 'pointer' : 'default', border: 0, padding: 0 }}
    >
      {ready ? (
        <StoryMedia item={clipItem(msg)} active muted fit="cover" />
      ) : (
        // The local file for the sender while it goes up (a video element paused at its poster);
        // the Cloudflare thumbnail for everyone once it exists.
        msg.videoThumbnailUrl && (status === 'uploading'
          // eslint-disable-next-line jsx-a11y/media-has-caption
          ? <video src={msg.videoThumbnailUrl} muted playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover" />
          : <img src={msg.videoThumbnailUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />)
      )}
      <span className="font-mono font-bold absolute" style={{ left: 10, bottom: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 9, letterSpacing: '.12em', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.6)' }}>
        {!ready && status !== 'failed' && <span aria-hidden className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
        {ready && <svg width="10" height="10" viewBox="0 0 24 24" fill="#fff" aria-hidden><path d="M6 3l15 9-15 9z" /></svg>}
        {line}
      </span>
    </button>
  );
}

/**
 * The clip full screen, with sound, looping inside its window — in the story video player
 * (handoff Part D): tap the halves to skip on the ramp, touch the bottom edge for the scrub
 * bar. No prev / next here, so there are no edge zones.
 */
export function VideoMessageViewer({ msg, onClose }) {
  const [muted, setMuted] = useState(false);
  const [coach, setCoach] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !coach) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, coach]);
  usePageScrollLock(true);
  // Swipe down to close, like the portal viewer.
  const frameRef = useRef(null);
  const swipe = useSwipeToDismiss(frameRef, { onDismiss: () => onClose?.() });
  const node = (
    <div ref={frameRef} data-testid="video-message-viewer" role="dialog" aria-modal="true" aria-label="Video" className="pr-overlay-root" style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#000', display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', ...swipe.style }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', flexShrink: 0, visibility: coach ? 'hidden' : 'visible' }}>
        <button type="button" onClick={() => setMuted((m) => !m)} className="font-mono font-bold" style={{ height: 36, padding: '0 14px', borderRadius: 999, border: '2px solid #fff', background: 'transparent', color: '#fff', fontSize: 10, letterSpacing: '.14em', cursor: 'pointer' }}>{muted ? 'SOUND OFF' : 'SOUND ON'}</button>
        <button type="button" onClick={onClose} className="font-mono font-bold" style={{ height: 36, padding: '0 16px', borderRadius: 999, border: '2px solid #fff', background: 'transparent', color: '#fff', fontSize: 10, letterSpacing: '.14em', cursor: 'pointer' }}>CLOSE</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <StoryVideoPlayer item={clipItem(msg)} active muted={muted} edgeZones={false} loop onCoachChange={setCoach} systemBarInset={false} />
      </div>
      <div className="font-mono" style={{ textAlign: 'center', padding: '12px 16px', fontSize: 9, letterSpacing: '.14em', color: 'rgba(255,255,255,.55)', flexShrink: 0, visibility: coach ? 'hidden' : 'visible' }}>{formatDuration(clipSeconds(msg))} · LOOPS · TAP A HALF TO SKIP</div>
    </div>
  );
  return createPortal(node, document.body);
}
